import { getAssociatedTokenAddressSync } from '@solana/spl-token';
import type { Connection, PublicKey } from '@solana/web3.js';
import type BN from 'bn.js';
import { estimateComputeUnits } from '../constants/cu-table.js';
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  MORFI_OPINION_PROGRAM_ID,
  SYSTEM_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import { deriveEventAuthorityPda } from '../pda/derivations.js';
import type { BuildParams, BuildResult } from '../types/build-result.js';
import {
  createBuildResult,
  createIdempotentAtaIx,
  invokeMethod,
  uniqueSignerKeys,
} from './common.js';
import { deriveMarketCreationAddresses } from './market-creation-addresses.js';

export interface CreateMarketParams extends BuildParams {
  creator: PublicKey;
  /** Rent sponsor; defaults to creator. Does not fund the backing. */
  payer?: PublicKey;
  marketConfig: PublicKey;
  marketNonce: BN;
  nOutcomes: number;
  quoteMint: PublicKey;
  /** Existing creator-owned quote account; defaults to an idempotently created creator ATA. */
  creatorQuoteAccount?: PublicKey;
  /** Net D6 backing; the contract charges the configured backing fee on top. */
  initialBacking: BN;
  /** Pass the current config admin signer while creation is restricted, otherwise null. */
  configAdmin: PublicKey | null;
}

/** Create a conviction market in Backing state. Outcome preparation completes readiness. */
export async function buildCreateMarket(
  connection: Connection,
  params: CreateMarketParams,
): Promise<BuildResult> {
  if (!Number.isInteger(params.nOutcomes) || params.nOutcomes < 2 || params.nOutcomes > 10) {
    throw new Error('Market creation requires 2..10 outcomes');
  }
  for (const [name, value] of [
    ['marketNonce', params.marketNonce],
    ['initialBacking', params.initialBacking],
  ] as const) {
    if (value.isNeg() || value.bitLength() > 64) throw new Error(`${name} must fit u64`);
  }
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const payer = params.payer ?? params.creator;
  const {
    pdaBumps,
    market,
    marketQuoteVault,
    backerShareMint,
    creatorBackerShareAta,
    platformFeeVault,
    adminConfig,
  } = deriveMarketCreationAddresses(
    params.creator,
    params.marketNonce,
    params.marketConfig,
    programId,
  );
  const program = await getProgram(connection, programId);
  const instruction = await invokeMethod(program, 'createMarket', {
    nOutcomes: params.nOutcomes,
    marketNonce: params.marketNonce,
    initialBacking: params.initialBacking,
    pdaBumps,
  })
    .accounts({
      market,
      marketQuoteVault,
      backerShareMint,
      creatorBackerShareAta,
      platformFeeVault,
      adminConfig,
      creator: params.creator,
      payer,
      marketConfig: params.marketConfig,
      quoteMint: params.quoteMint,
      creatorQuoteAccount:
        params.creatorQuoteAccount ??
        getAssociatedTokenAddressSync(params.quoteMint, params.creator),
      configAdmin: params.configAdmin,
      eventSchedule: null,
      systemProgram: SYSTEM_PROGRAM_ID,
      tokenProgram: TOKEN_PROGRAM_ID,
      associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
      eventAuthority: deriveEventAuthorityPda(programId)[0],
      program: programId,
    })
    .instruction();
  const signers =
    params.configAdmin === null
      ? [params.creator, payer]
      : [params.creator, payer, params.configAdmin];
  const instructions =
    params.creatorQuoteAccount === undefined
      ? [
          createIdempotentAtaIx(payer, params.creator, params.quoteMint, TOKEN_PROGRAM_ID),
          instruction,
        ]
      : [instruction];
  return createBuildResult(
    instructions,
    uniqueSignerKeys(...signers),
    params.computeUnits ?? estimateComputeUnits('create_market'),
  );
}
